'use client'

// HudMenu, one button in the corner that fans out into the whole site.
//
// It used to be five unlabelled circles. That is fine for someone who has
// been here before and hostile to everybody else: a head and shoulders is
// not self-explanatory to somebody on their first visit.
// So the fan is named now, and the world dims behind it so the names are
// readable over whatever happens to be on screen.
//
// The icons keep their circles and the names sit OUTSIDE them, in white on
// the dimmed world. Putting the label inside the bubble turned every button
// into a wide slab, and a column of slabs doesn't read as a fan. The rows
// are a fixed width hanging off the right edge, which is what keeps the
// circles in a straight column under the hamburger.
//
// The badge lives on the hamburger while it's closed (so an unread diary
// entry or a new verdict still reaches you), and moves onto the specific
// button once it's open, so you can tell which one wants you.

import { Fragment, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import useGameStore from '../../stores/gameStore'
import { FEATURES, HAS_AUTH } from '../../lib/features'
import { useAccount } from '../auth/useUser'
import { ShopIcon, BookIcon, AskIcon, GiftIcon, JobsIcon } from './KawaiiButton'
import { kawaii, ui } from '../../lib/ui/theme'
import { useIsMobile } from '../../lib/ui/useIsMobile'
import { NoticeBadge } from './Notices'
import { profileNotices, NOTICE, activeNotices } from '../../lib/ui/notices'
import { useDerivedNotices } from './NoticeWatcher'
import { BIRD_NAME } from '../../lib/world/identity'

// ── First-open tour ─────────────────────────────────────────────────────
//
// Shown once, the first time the menu unfolds, then never again (a flag
// in localStorage). To change the tour, edit this list; to add a step,
// add a row. Nothing else needs touching.
const MENU_TOUR = [
  {
    title: 'his diary',
    body: `what ${BIRD_NAME} did and what he made of it, in his own words. once you're connected there's a page of every line that mentions you.`,
  },
  {
    title: 'his shop',
    body: 'every drawing he makes, printed once as a card, a tenner each. some are printed and ready to post, the rest are printed when you ask.',
  },
  {
    title: 'jobs',
    body: "he's saved up for a train but has no plan, so he puts up small jobs at the job centre: a picture of something to do with trains, paid from his train money. take one and send it from your phone.",
  },
  {
    title: 'leave him something',
    body: 'a message or a picture. it lands in his world where you stand, he finds it in his own time, and he decides whether it stays with him.',
  },
  {
    title: "what's on",
    body: 'films, gigs and exhibitions are scheduled ahead. he reads the posters and makes his own plans. know him well enough and you can put things on.',
  },
  {
    title: 'you',
    body: 'connect with just an email. the more of yours he keeps, the better he knows you when you come back.',
  },
]
const TOUR_KEY = 'pino-menu-tour-seen'

function MenuTour({ onDone, below = null }) {
  const [step, setStep] = useState(0)
  const it = MENU_TOUR[step]
  const last = step === MENU_TOUR.length - 1
  return (
    <div
      data-menu-tour
      className="fixed z-[60] pointer-events-auto"
      style={{
        // Beside the fan when there is room. On a phone it sat ON the fan
        // (measured 23 Sep: the card covered the first four labels), so
        // there it drops under the last button instead.
        top: below ?? 130,
        right: below != null ? 12 : 'min(232px, max(12px, calc(100vw - 292px)))',
        width: below != null ? 'min(86vw, 320px)' : 'min(74vw, 280px)',
        background: kawaii.cream,
        border: kawaii.outline,
        borderRadius: 16,
        boxShadow: kawaii.drop,
        padding: '13px 15px',
        fontFamily: ui.font,
      }}
    >
      <p style={{ color: kawaii.ink, fontSize: 14.5, fontWeight: 800, marginBottom: 4 }}>{it.title}</p>
      <p style={{ color: kawaii.inkSoft, fontSize: 13, fontWeight: 600, lineHeight: 1.55 }}>{it.body}</p>
      <div className="flex items-center justify-between" style={{ marginTop: 11 }}>
        <span className="flex items-center gap-1.5">
          {MENU_TOUR.map((_, i) => (
            <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: i === step ? kawaii.ink : 'rgba(47,38,80,0.25)' }} />
          ))}
        </span>
        <span className="flex items-center gap-2">
          <button type="button" onClick={onDone} style={{ color: kawaii.inkSoft, fontSize: 12, fontWeight: 800 }}>
            skip
          </button>
          <button
            type="button"
            onClick={() => (last ? onDone() : setStep(step + 1))}
            style={{
              background: kawaii.ink, color: kawaii.cream, border: kawaii.outlineThin,
              borderRadius: 999, padding: '4px 14px', fontSize: 12.5, fontWeight: 800,
            }}
          >
            {last ? 'got it' : 'next'}
          </button>
        </span>
      </div>
    </div>
  )
}

const SIZE = 42
const GAP = 10               // between fanned buttons
const STEP = SIZE + GAP
const ROW_W = 210            // circle plus room for the longest name

// Head and shoulders. Deliberately not a photo-avatar shape: this is your
// standing in his town, not an account settings page.
function ProfileGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="6.2" r="3.1" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.2 15.2c0-2.8 2.6-4.6 5.8-4.6s5.8 1.8 5.8 4.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

// A page-a-day calendar: ring binding, one date block. Reads at 18px.
function CalendarGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2.2" y="3.6" width="13.6" height="11.6" rx="2.4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M5.6 2v3M12.4 2v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2.6 7.4h12.8" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9.6" y="9.4" width="3.4" height="3.2" rx="0.8" fill="currentColor" />
    </svg>
  )
}

function MoreGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="4" cy="9" r="1.5" fill="currentColor" />
      <circle cx="9" cy="9" r="1.5" fill="currentColor" />
      <circle cx="14" cy="9" r="1.5" fill="currentColor" />
    </svg>
  )
}

export function HudMenu({ onAbout, journalUnseen, shrineUnseen }) {
  const isMobile = useIsMobile()
  const menuOpen = useGameStore((s) => s.menuOpen)
  const toggleMenu = useGameStore((s) => s.toggleMenu)
  const setMenuOpen = useGameStore((s) => s.setMenuOpen)
  const setJournalOpen = useGameStore((s) => s.setJournalOpen)
  const setShrineOpen = useGameStore((s) => s.setShrineOpen)
  const setProfileOpen = useGameStore((st) => st.setProfileOpen)
  const setCalendarOpen = useGameStore((st) => st.setCalendarOpen)
  const setPostersOpen = useGameStore((st) => st.setPostersOpen)
  const setJobsOpen = useGameStore((st) => st.setJobsOpen)
  const setCalendarToVote = useGameStore((st) => st.setCalendarToVote)
  // Whether the poster shelf is worth offering. Set by the calendar drawer
  // when it reads your standing, so this does not fetch it a second time.
  const canPoster = useGameStore((st) => st.canPoster)
  // Offerings judged, rank moves. Colour-coded so the corner says WHICH
  // thing is waiting before you have opened anything, and the tab inside
  // repeats the same colour.
  // Derived, not the raw flags: a dot has to mean "since you last looked",
  // which is a comparison, not an event somebody had to be present for.
  const notices = useDerivedNotices()
  const profileBadges = profileNotices({ notices, shrineUnseen })
  const [moreOpen, setMoreOpen] = useState(false)
  // The first unfold gets the tour. Read lazily so SSR never touches
  // localStorage, latched so it can only ever fire once per browser.
  const [tourOpen, setTourOpen] = useState(false)
  useEffect(() => {
    if (!menuOpen) return
    let seen = true
    try { seen = window.localStorage.getItem(TOUR_KEY) === '1' } catch { /* private mode */ }
    if (!seen) setTourOpen(true)
  }, [menuOpen])
  const endTour = () => {
    setTourOpen(false)
    try { window.localStorage.setItem(TOUR_KEY, '1') } catch { /* fine */ }
  }
  const rootRef = useRef(null)

  // Clicking anywhere else folds it back, so it never sits open forgotten.
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e) => {
      // The tour card floats outside the fan on purpose, so clicking
      // "next" must not read as clicking away. Found the hard way: the
      // first press advanced the step AND folded the menu, which
      // unmounted the tour at stage one.
      if (e.target.closest?.('[data-menu-tour]')) return
      if (rootRef.current && !rootRef.current.contains(e.target)) setMenuOpen(false)
    }
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false) }
    window.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [menuOpen, setMenuOpen])

  const pick = (fn) => () => { setMenuOpen(false); fn() }

  // On the way: somebody who tapped leave him something while signed out
  // is sent to the connect card. The moment they are signed in, the drawer
  // they were heading for opens itself, instead of leaving them on a
  // settings page to find it again.
  const { signedIn } = useAccount()
  const connectThen = useGameStore((st) => st.connectThen)
  const setConnectThen = useGameStore((st) => st.setConnectThen)
  useEffect(() => {
    if (!signedIn || !connectThen) return
    setConnectThen(null)
    if (connectThen === 'shrine') { setProfileOpen(false); setShrineOpen(true) }
    // (and give him something, from his bag: components/ui/BagPanel.jsx)
    if (connectThen === 'bag') { setProfileOpen(false); useGameStore.getState().setBagOpen(true) }
  }, [signedIn, connectThen, setConnectThen, setProfileOpen, setShrineOpen])

  // The doors, in the order the tour names them and in the words it uses.
  // They were About, Journal, Profile, Offerings, Calendar: five words that
  // matched neither the tour ("his diary", "leave him something", "the town
  // moves", "he remembers people") nor the drawers they opened ("Pino's
  // diary", "you, in his town"). One set of words now, on the button, in
  // the tour and on the drawer. About lives in the deeper list only; it
  // was in both.
  const items = [
    { id: 'journal', label: 'His diary', icon: <BookIcon />, badge: journalUnseen, run: () => setJournalOpen(true) },
    // (25 Oct, Sam: the shop a prominent part of the site) a page, not a drawer: it leaves the world
    { id: 'shop', label: 'His shop', icon: <ShopIcon />, badge: false, run: () => { window.location.assign('/shop') } },
    // (30 Oct, Sam: "lets get the JOBS added to the main menu under the His Shop link...this can open up the jobs popup")
    // the job centre's overlay, from wherever you are, the way ?jobs opens it (components/jobs/JobCentreHud.jsx)
    { id: 'jobs', label: 'Jobs', icon: <JobsIcon />, badge: false, run: () => setJobsOpen(true) },
  ]
  // Opens a drawer that page.jsx only mounts when HAS_AUTH is true, so
  // without it the door was there and led nowhere. Same condition on both
  // sides.
  if (FEATURES.shrine && HAS_AUTH) {
    items.push({ id: 'offerings', label: 'Leave him something', icon: <GiftIcon />, badge: shrineUnseen, run: () => setShrineOpen(true) })
  }
  items.push({
    id: 'calendar',
    label: "What's on",
    icon: <CalendarGlyph />,
    badge: notices.calendar,
    // A lit dot here means one thing only: a ballot nobody has shown you
    // yet. So say so on the way in, and the drawer opens on the vote
    // instead of on what's on. Without this the ping led to a page about
    // something else and marked itself read for the trouble.
    run: () => { setCalendarToVote(notices.calendar); setCalendarOpen(true) },
  })
  if (HAS_AUTH) {
    items.push({
      id: 'profile',
      label: 'You',
      icon: <ProfileGlyph />,
      notices: profileBadges,
      run: () => setProfileOpen(true),
    })
  }
  // The only one with no label: it is a lid on the rest of the site rather
  // than a destination, and naming it "more" twice over (icon and word)
  // would be the noisiest thing in the column.
  items.push({ id: 'more', label: null, icon: <MoreGlyph />, badge: false, run: () => setMoreOpen(true) })

  // One dot per waiting thing, in the same colours the tabs inside use,
  // so the corner says WHICH doors have something before any of them is
  // open. The journal keeps its own pink; the shrine folds into the
  // offerings orange like it does everywhere else. Capped where the
  // palette is capped: four notice colours plus the journal.
  const cornerDots = []
  if (journalUnseen) cornerDots.push('#f2748a')
  for (const k of activeNotices({ ...notices, offerings: notices.offerings || shrineUnseen })) {
    cornerDots.push(NOTICE[k].color)
  }

  return (
    <>
      {/* The world, dimmed under the menu. Darkest on the right where the
          pills are and gone by the middle of the screen, so the names stay
          readable over a bright sky without blacking out the thing people
          came to look at. */}
      <div
        className="fixed inset-0 z-40"
        style={{
          background: 'linear-gradient(to left, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.34) 28%, rgba(0,0,0,0) 62%)',
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'auto' : 'none',
          transition: 'opacity 260ms ease',
        }}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <div
        ref={rootRef}
        className="fixed top-[74px] right-3 md:top-16 md:right-6 z-50 pointer-events-auto"
        style={{ width: SIZE, height: SIZE }}
      >
        {/* Always mounted so the fold-back animates too; closed they sit
            under the hamburger at zero scale. */}
        {items.map((it, i) => (
          <button
            key={it.id}
            type="button"
            aria-label={it.label || 'more'}
            title={it.label || 'more'}
            onClick={pick(it.run)}
            tabIndex={menuOpen ? 0 : -1}
            style={{
              // The button itself is invisible and wide: the label lives
              // inside it so clicking a name works, but only the circle and
              // the white word are drawn.
              position: 'absolute',
              top: 0,
              right: 0,
              width: it.label ? ROW_W : SIZE,
              height: SIZE,
              background: 'transparent',
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 12,
              cursor: 'pointer',
              // Fold back onto the HAMBURGER, not onto the middle of the
              // row. Without moving the origin over the circle the whole
              // strip shrinks into the empty space where the label was.
              transformOrigin: `calc(100% - ${SIZE / 2}px) 50%`,
              transform: menuOpen
                ? `translateY(${STEP * (i + 1)}px) scale(1)`
                : 'translateY(0) scale(0.4)',
              opacity: menuOpen ? 1 : 0,
              pointerEvents: menuOpen ? 'auto' : 'none',
              transition: `transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1) ${menuOpen ? i * 45 : (items.length - 1 - i) * 30}ms, opacity 200ms ease ${menuOpen ? i * 45 : 0}ms`,
            }}
          >
            {it.label && (
              <span
                style={{
                  color: '#ffffff',
                  fontFamily: ui.font,
                  fontSize: 14.5,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  // A breath of shadow for the moment the gradient happens to
                  // sit over a pale sky and white would vanish into it. On a
                  // phone the gradient's dark band is only ~100px wide, so
                  // the words sit over the raw world and shadow alone loses;
                  // they get a proper pill instead.
                  ...(isMobile
                    ? {
                        background: 'rgba(21,15,44,0.62)',
                        padding: '5px 12px',
                        borderRadius: 999,
                        backdropFilter: 'blur(4px)',
                      }
                    : { textShadow: '0 1px 6px rgba(0,0,0,0.55)' }),
                }}
              >
                {it.label}
              </span>
            )}
            <span
              style={{
                position: 'relative',
                width: SIZE,
                height: SIZE,
                flexShrink: 0,
                borderRadius: '50%',
                background: kawaii.cream,
                border: kawaii.outline,
                boxShadow: kawaii.dropSm,
                color: kawaii.ink,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {it.icon}
              {it.notices ? <NoticeBadge notices={it.notices} /> : (it.badge && <Dot />)}
            </span>
          </button>
        ))}

        {/* The pill beside the hamburger. The hamburger is flush to the
            right edge, so the pill hangs off the root's LEFT. Signed out it
            is the product, leave him something, and the drawer it opens
            asks you to connect on the way; signed in it is your name, and
            opens you. */}
        {HAS_AUTH && (
          <ConnectPill
            onYou={pick(() => setProfileOpen(true))}
            onLeave={pick(() => (FEATURES.shrine ? setShrineOpen(true) : setProfileOpen(true)))}
          />
        )}

        {/* The hamburger itself. Bars morph toward a cross while open. */}
        <button
          type="button"
          aria-label={menuOpen ? 'close menu' : 'open menu'}
          aria-expanded={menuOpen}
          title="menu"
          onClick={toggleMenu}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: SIZE,
            height: SIZE,
            borderRadius: '50%',
            background: menuOpen ? kawaii.creamDeep : kawaii.cream,
            border: kawaii.outline,
            boxShadow: kawaii.dropSm,
            color: kawaii.ink,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            cursor: 'pointer',
            transition: 'background 160ms ease',
          }}
        >
          <Bars open={menuOpen} />
          {!menuOpen && cornerDots.length > 0 && <Dots colors={cornerDots} />}
        </button>

        <span style={{ fontFamily: ui.font, display: 'none' }} aria-hidden="true" />
      </div>

      {tourOpen && menuOpen && (
        <MenuTour onDone={endTour} below={isMobile ? 74 + STEP * items.length + SIZE + 10 : null} />
      )}

      <MoreMenu
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onAbout={onAbout}
        onPosters={() => setPostersOpen(true)}
        canPoster={HAS_AUTH && canPoster}
      />
    </>
  )
}

// ── The more menu ───────────────────────────────────────────────────────

// The deeper rooms. Anything without a href is not built yet and is listed
// anyway rather than hidden, because what is coming is part of what this
// place is. The two reading rooms are real pages now: they leave the world
// rather than opening a drawer, which is the right weight for them, they
// are long and you go there to read.
const MORE_ITEMS = [
  { id: 'about', label: 'what this is', ready: true },
  { id: 'shop', label: 'his shop', ready: true, href: '/shop', page: true },
  { id: 'days', label: 'his days', ready: true, href: '/logs', page: true },
  { id: 'brain', label: 'his brain', ready: true, href: '/brain', page: true },
  { id: 'memories', label: 'his memories', ready: true, href: '/memories', page: true },
  // What decides what he does, and the raw data to check it. A check of
  // him from outside (Larpcheck, 2 Nov) could not tell a model from a script.
  { id: 'facts', label: 'how he works', ready: true, href: '/facts', page: true },
  // His own websites, the ones he writes and publishes himself. Until now
  // they were reachable only by standing at nine places in town.
  { id: 'sites', label: 'his sites', ready: true, expands: true },
  // A drawer rather than a page, so it opens over the world instead of
  // leaving it. Only there once he knows you: nothing to hold otherwise.
  { id: 'posters', label: 'your posters', ready: true, opens: 'posters' },
]

const soft = 'rgba(247,231,210,0.55)'

function MoreMenu({ open, onClose, onAbout, onPosters, canPoster }) {
  const [hovered, setHovered] = useState(null)
  const [sitesOpen, setSitesOpen] = useState(false)
  const [sites, setSites] = useState(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // His addresses, read once per opening. Short, and it changes when he
  // publishes, which is days apart.
  useEffect(() => {
    if (!open) return
    setSitesOpen(false)
    let dead = false
    fetch('/api/sites', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (dead) return
        const list = Array.isArray(d) ? d : (Array.isArray(d?.sites) ? d.sites : [])
        setSites(list.filter((site) => site?.url && site?.title))
      })
      .catch(() => { if (!dead) setSites([]) })
    return () => { dead = true }
  }, [open])

  const entries = MORE_ITEMS.filter((it) => it.id !== 'posters' || canPoster)

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center"
      style={{
        background: 'rgba(6,4,12,0.94)',
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        transition: 'opacity 300ms ease',
      }}
      onClick={onClose}
    >
      <p
        style={{
          color: soft, fontFamily: ui.font, fontSize: 13, fontWeight: 800, letterSpacing: '0.04em', marginBottom: 8,
          opacity: open ? 1 : 0, transition: 'opacity 320ms ease',
        }}
      >
        the rest of him
      </p>
      <nav className="flex flex-col items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {entries.map((it, i) => {
          const dim = !it.ready
          // A link when it goes somewhere, a button when it opens something
          // here. Rendering the pages as buttons with router.push would
          // cost middle-click, open-in-new-tab and the status bar preview,
          // on the two pages people are most likely to want to keep open.
          const Tag = it.href ? Link : 'button'
          return (
            <Fragment key={it.id}>
              <Tag
                {...(it.href
                  ? { href: it.href, onClick: () => onClose() }
                  : {
                      type: 'button',
                      disabled: dim,
                      onClick: () => {
                        if (!it.ready) return
                        if (it.expands) { setSitesOpen((v) => !v); return }
                        onClose()
                        if (it.opens === 'posters') onPosters?.()
                        else onAbout?.()
                      },
                    })}
                onMouseEnter={() => setHovered(it.id)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  position: 'relative',
                  padding: '10px 26px',
                  background: 'transparent',
                  border: 'none',
                  color: dim ? 'rgba(247,231,210,0.42)' : kawaii.cream,
                  fontFamily: ui.font,
                  fontSize: 26,
                  fontWeight: 800,
                  letterSpacing: '-0.01em',
                  cursor: dim ? 'default' : 'pointer',
                  // Same staggered arrival as the fan, so the two menus feel
                  // like one gesture rather than two components.
                  transform: open ? 'translateY(0)' : 'translateY(14px)',
                  opacity: open ? 1 : 0,
                  transition: `transform 420ms cubic-bezier(0.22,1,0.36,1) ${i * 70}ms, opacity 320ms ease ${i * 70}ms, color 160ms ease`,
                }}
              >
                {it.label}
                {/* Says which entries leave the world for a page. */}
                {it.page && (
                  <span style={{ marginLeft: 9, fontSize: 11, fontWeight: 800, color: soft, letterSpacing: '0.04em', verticalAlign: 'middle' }}>page</span>
                )}
                {it.expands && (
                  <span style={{ marginLeft: 9, fontSize: 11, fontWeight: 800, color: soft, letterSpacing: '0.04em', verticalAlign: 'middle' }}>
                    {sitesOpen ? 'close' : sites?.length ? `${sites.length}` : ''}
                  </span>
                )}
                {dim && hovered === it.id && (
                  <span
                    style={{
                      position: 'absolute',
                      left: '100%',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      marginLeft: 10,
                      fontSize: 12,
                      fontWeight: 800,
                      color: '#f0b064',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    coming soon
                  </span>
                )}
              </Tag>
              {it.expands && sitesOpen && (
                <div className="flex flex-col items-center" style={{ marginBottom: 6 }}>
                  {sites === null && (
                    <span style={{ color: soft, fontFamily: ui.font, fontSize: 13, fontWeight: 700, padding: '4px 0' }}>reading his addresses...</span>
                  )}
                  {sites?.length === 0 && (
                    <span style={{ color: soft, fontFamily: ui.font, fontSize: 13, fontWeight: 700, padding: '4px 0' }}>nothing published yet</span>
                  )}
                  {(sites || []).map((site) => (
                    <a
                      key={site.url}
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: kawaii.cream, fontFamily: ui.font, fontSize: 17, fontWeight: 800, padding: '5px 26px',
                        opacity: site.archived ? 0.6 : 1,
                      }}
                    >
                      {site.title}
                    </a>
                  ))}
                </div>
              )}
            </Fragment>
          )
        })}
      </nav>

      <button
        type="button"
        onClick={onClose}
        style={{
          marginTop: 30,
          color: soft,
          fontFamily: ui.font,
          fontSize: 13,
          fontWeight: 700,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          opacity: open ? 1 : 0,
          transition: `opacity 320ms ease ${MORE_ITEMS.length * 70}ms`,
        }}
      >
        close
      </button>
    </div>
  )
}

function ConnectPill({ onYou, onLeave }) {
  const { ready, signedIn, username } = useAccount()

  // Who is this, before the island can say: privy leaves its session token
  // in localStorage, so its presence is the tell. A returning visitor gets
  // bars that resolve straight to their name; a new visitor gets the door
  // instantly, because there is nothing to check.
  // Read in an effect so the first paint matches the server's.
  const [hint, setHint] = useState('unknown')
  useEffect(() => {
    let returning = false
    try {
      returning = Object.keys(window.localStorage).some((k) => k.startsWith('privy:') && k.includes('token'))
    } catch { /* private mode etc, treat as new */ }
    setHint(returning ? 'returning' : 'new')
  }, [])

  // And the harder give-up: privy never reaching ready at all (mangled
  // storage, blocked network, an adblocker that eats their SDK). Bars that
  // never resolve are worse than the door, so past this point the pill
  // stops pretending it knows you.
  const [gaveUp, setGaveUp] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setGaveUp(true), 8000)
    return () => clearTimeout(t)
  }, [])

  const settled = ready && (!signedIn || !!username)
  const showBars = !gaveUp && (hint === 'unknown' || (hint === 'returning' && !settled))

  if (showBars) {
    return (
      <span
        style={{
          position: 'absolute',
          top: 0,
          right: SIZE + GAP,
          height: SIZE,
          width: 128,
          borderRadius: 999,
          background: kawaii.cream,
          border: kawaii.outline,
          boxShadow: kawaii.dropSm,
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
        }}
      >
        <style>{`@keyframes pill-pulse { 0%, 100% { opacity: .4 } 50% { opacity: .85 } }`}</style>
        <span style={{ height: 11, width: '100%', borderRadius: 8, background: kawaii.creamDeep, animation: 'pill-pulse 1.3s ease infinite' }} />
      </span>
    )
  }

  // Signed out, the pill is the product: leave him something. It opens the
  // offerings drawer, which asks you to connect on the way. It said
  // "connect to Pino" before, which is a means, not a reason, and the most
  // prominent button on the screen led to a settings card.
  const label = signedIn ? (username || 'you') : 'leave him something'
  return (
    <button
      type="button"
      onClick={signedIn ? onYou : onLeave}
      title={signedIn ? 'you, in his town' : `leave ${BIRD_NAME} something, he will remember you`}
      style={{
        position: 'absolute',
        top: 0,
        right: SIZE + GAP,
        height: SIZE,
        borderRadius: 999,
        background: signedIn ? kawaii.cream : '#f0b064',
        border: kawaii.outline,
        boxShadow: kawaii.dropSm,
        color: kawaii.ink,
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '0 16px',
        fontFamily: ui.font,
        fontSize: 13.5,
        fontWeight: 800,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
    >
      {signedIn && (
        <span
          style={{
            width: 8, height: 8, borderRadius: 999, background: '#a8d6a0',
            border: `1.5px solid ${kawaii.ink}`, flexShrink: 0,
          }}
        />
      )}
      {label}
    </button>
  )
}

/** Three bars, or a cross. Drawn as two states that swap rather than one set
 *  of lines animated into position: the bars sit at y=7.5 and y=16.5, so
 *  getting them to meet in the middle needs a translate AND a rotate, and CSS
 *  applies those right-to-left, which put the cross off-centre. Two clean
 *  states crossfading is simpler and lands exactly where it should. */
function Bars({ open }) {
  const line = {
    stroke: kawaii.ink,
    strokeWidth: 2.6,
    strokeLinecap: 'round',
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <g
        style={{
          opacity: open ? 0 : 1,
          transform: open ? 'scale(0.6)' : 'scale(1)',
          transformOrigin: '12px 12px',
          transition: 'opacity 140ms ease, transform 240ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <line x1="4.5" y1="7.5" x2="19.5" y2="7.5" {...line} />
        <line x1="4.5" y1="12" x2="19.5" y2="12" {...line} />
        <line x1="4.5" y1="16.5" x2="19.5" y2="16.5" {...line} />
      </g>
      <g
        style={{
          opacity: open ? 1 : 0,
          transform: open ? 'scale(1) rotate(0deg)' : 'scale(0.5) rotate(-90deg)',
          transformOrigin: '12px 12px',
          transition: 'opacity 160ms ease 60ms, transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        <line x1="6.8" y1="6.8" x2="17.2" y2="17.2" {...line} />
        <line x1="17.2" y1="6.8" x2="6.8" y2="17.2" {...line} />
      </g>
    </svg>
  )
}

// The corner cluster: newest-style multi-dot, one per lit notice colour.
// Overlapped slightly so five dots still read as a huddle on the corner
// of a 40px button rather than a necklace across it.
function Dots({ colors }) {
  return (
    <span style={{ position: 'absolute', top: -5, right: -5, display: 'flex', flexDirection: 'row-reverse' }}>
      {colors.map((c, i) => (
        <span
          key={`${c}-${i}`}
          style={{
            width: 13,
            height: 13,
            borderRadius: '50%',
            background: c,
            border: `2px solid ${kawaii.ink}`,
            marginLeft: i === colors.length - 1 ? 0 : -5,
          }}
        />
      ))}
    </span>
  )
}

function Dot() {
  return (
    <span
      style={{
        position: 'absolute',
        top: -5,
        right: -5,
        width: 13,
        height: 13,
        borderRadius: '50%',
        background: '#f2748a',
        border: `2px solid ${kawaii.ink}`,
      }}
    />
  )
}

export default HudMenu
